import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { slideUp } from '@/lib/motion';
import {
  UserCircleIcon,
  BuildingOfficeIcon,
  KeyIcon,
  LinkIcon,
  BellIcon,
} from '@heroicons/react/24/outline';
import GeneralSettings from '../../components/settings/GeneralSettings';
import SecuritySettings from '../../components/settings/SecuritySettings';
import NotificationsSettings from '../../components/settings/NotificationsSettings';
import LearnerOrganizationCard from '../../components/learner/LearnerOrganizationCard';
import LearnerIntegrationsSettings from './settings/IntegrationsSettings';

type LearnerSettingsTab =
  | 'general'
  | 'security'
  | 'notifications'
  | 'organization'
  | 'integrations';

const TABS: Array<{ id: LearnerSettingsTab; label: string; icon: React.ElementType }> = [
  { id: 'general', label: 'General', icon: UserCircleIcon },
  { id: 'security', label: 'Security', icon: KeyIcon },
  { id: 'notifications', label: 'Notifications', icon: BellIcon },
  { id: 'organization', label: 'Organisation', icon: BuildingOfficeIcon },
  { id: 'integrations', label: 'Integrations', icon: LinkIcon },
];

const LearnerSettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = (searchParams.get('tab') as LearnerSettingsTab) || 'general';

  const handleTabChange = (tabId: LearnerSettingsTab) => {
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'general':
        return <GeneralSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'notifications':
        return <NotificationsSettings />;
      case 'organization':
        return <LearnerOrganizationCard />;
      case 'integrations':
        return <LearnerIntegrationsSettings />;
      default:
        return <GeneralSettings />;
    }
  };

  return (
    <motion.div {...slideUp} className="px-0 lg:px-4 py-2 lg:py-6">
      {/* Title + Breadcrumbs */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-text-primary mb-1">Account</h1>
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/learn/dashboard"
            className="text-text-primary hover:text-primary-500 transition-colors"
          >
            Dashboard
          </Link>
          <span className="w-1 h-1 rounded-full bg-text-primary inline-block" />
          <span className="text-text-disabled">Account</span>
        </div>
      </div>

      {/* Tabs */}
      {/* overflow-y-hidden: the -mb-px nav hangs 1px below this scroll container;
          without it a dead 1px scrollbar renders next to the tabs. */}
      <div className="border-b border-border-primary/20 mb-8 overflow-x-auto overflow-y-hidden">
        <nav className="flex min-w-max -mb-px">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = currentTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  active
                    ? 'border-text-primary text-text-primary'
                    : 'border-transparent text-text-tertiary hover:text-text-primary'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      <div>{renderTabContent()}</div>
    </motion.div>
  );
};

export default LearnerSettingsPage;
